/**
 * @fileoverview Record Change of Appointment Dialog (MOR-901)
 *
 * Purpose: Modal that records a change of appointment on an already-appointed
 * job — a voluntary administration converting to a CVL, a deed being executed,
 * a deed failing into a liquidation.
 *
 * Exists because a conversion is not a field edit. The new appointment has its
 * own date, so repointing the appointment type on its own would leave the job
 * claiming the liquidation began the day the administration did. Capturing the
 * date here is mandatory, and the outgoing appointment is preserved on the
 * job's appointment history.
 *
 * Dependencies:
 * - Convex: jobs.mutations.recordAppointmentChange
 *
 * Used by:
 * - Appointment tab of job settings, when the appointment type is changed.
 */

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { Button } from "@/ui/primitives/button";
import { Label } from "@/ui/primitives/label";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { ArrowRight, Loader2 } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { AppointmentType } from "@cvx/jobs/schema";
import { toast } from "sonner";

/** Mirrors the labels used by the create-job and convert dialogs. */
const APPOINTMENT_TYPE_LABELS: Record<string, string> = {
  cvl: "Creditors Voluntary Liquidation (CVL)",
  mvl: "Members Voluntary Liquidation (MVL)",
  simplified_liquidation: "Simplified Liquidation",
  court_liquidation: "Court Liquidation",
  provisional_liquidation: "Provisional Liquidation",
  sbr: "Small Business Restructuring (SBR)",
  voluntary_administration: "Voluntary Administration (VA)",
  deed_of_company_arrangement: "Deed of Company Arrangement (DOCA)",
  receivership: "Receivership",
  scheme_of_arrangement: "Scheme of Arrangement",
  personal_bankruptcy: "Personal Bankruptcy",
  business_bankruptcy: "Business Bankruptcy",
  creditors_petition: "Creditors Petition",
  debtors_petition: "Debtors Petition",
  part_x_arrangement: "Personal Insolvency Agreement (Part X)",
};

/**
 * Types whose relation-back day traces to the day the administration began,
 * mirroring `TRACES_TO_ADMINISTRATION` in `convex/jobs/appointmentSwitch.ts`.
 * Used only to explain the auto-carry to the practitioner.
 */
const TRACES_TO_ADMINISTRATION = new Set([
  "cvl",
  "simplified_liquidation",
  "court_liquidation",
  "deed_of_company_arrangement",
]);

interface RecordAppointmentChangeDialogProps {
  jobId: Id<"jobs">;
  /** The appointment being replaced. */
  currentAppointmentType: string;
  currentAppointmentDate?: string;
  /** Pre-selected new type — the value the user picked in the settings list. */
  initialAppointmentType?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RecordAppointmentChangeDialog({
  jobId,
  currentAppointmentType,
  currentAppointmentDate,
  initialAppointmentType,
  open,
  onOpenChange,
}: RecordAppointmentChangeDialogProps) {
  const [appointmentType, setAppointmentType] = useState<string>(
    initialAppointmentType ?? "",
  );
  const [appointmentDate, setAppointmentDate] = useState<string>("");
  const [note, setNote] = useState<string>("");

  // Reset on re-open so a previous abandoned attempt doesn't leak through.
  useEffect(() => {
    if (!open) return;
    setAppointmentType(initialAppointmentType ?? "");
    setAppointmentDate("");
    setNote("");
  }, [open, initialAppointmentType]);

  const { mutateAsync: recordChange, isPending } = useMutation({
    mutationFn: useConvexMutation(api.jobs.mutations.recordAppointmentChange),
  });

  const isSameType = appointmentType === currentAppointmentType;
  const canSubmit = !!appointmentType && !!appointmentDate && !isSameType;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await recordChange({
        jobId,
        appointmentType: appointmentType as AppointmentType,
        appointmentDate,
        note: note.trim() || undefined,
      });
      toast.success("Change of appointment recorded");
      onOpenChange(false);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to record the change of appointment";
      toast.error(message);
    }
  };

  const carriesAdministrationDate =
    currentAppointmentType === "voluntary_administration" &&
    TRACES_TO_ADMINISTRATION.has(appointmentType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Record Change of Appointment</DialogTitle>
          <DialogDescription>
            The current appointment is kept on the job's appointment history, so
            its dates stay available to document generation and the
            relation-back day.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          {/* What is being replaced — reassures the user history is kept. */}
          <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
            <span className="font-medium">
              {APPOINTMENT_TYPE_LABELS[currentAppointmentType] ??
                currentAppointmentType}
            </span>
            {currentAppointmentDate && (
              <span className="text-muted-foreground">
                {currentAppointmentDate}
              </span>
            )}
            <ArrowRight className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="text-muted-foreground">
              {appointmentType
                ? (APPOINTMENT_TYPE_LABELS[appointmentType] ?? appointmentType)
                : "Select the new appointment"}
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <Label>New Appointment Type</Label>
            <Select value={appointmentType} onValueChange={setAppointmentType}>
              <SelectTrigger>
                <SelectValue placeholder="Select appointment type" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(APPOINTMENT_TYPE_LABELS).map(
                  ([value, label]) => (
                    <SelectItem
                      key={value}
                      value={value}
                      disabled={value === currentAppointmentType}
                    >
                      {label}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label>
              Date of New Appointment
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <DatePicker
              value={appointmentDate}
              onChange={setAppointmentDate}
              placeholder="Select the date the new appointment took effect"
            />
            <p className="text-xs text-muted-foreground">
              The day the new appointment took effect — for a CVL following an
              administration, the date of the creditors' resolution, not the
              date the administrators were appointed.
            </p>
          </div>

          {carriesAdministrationDate && (
            <p className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
              The administration start date (the s513C day) will be carried
              forward from this administration, so the relation-back day
              continues to trace to it.
            </p>
          )}

          <div className="flex flex-col gap-2">
            <Label>Note (optional)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Deed terminated by creditors' resolution; company wound up"
              rows={2}
            />
          </div>

          <p className="text-xs text-muted-foreground">
            Upload the new instrument of appointment from the Notice of
            Appointment panel below once this is recorded.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isPending}>
            {isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            Record Change
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
